// Create a new file LineItems.jsx with fixes
import React, { Component } from 'react';
import Form from 'react-validation/build/form';
import { required, isNumeric, isCurrency } from "../../../util/validationMethods";

class LineItems extends Component {
  constructor(props) {
    super(props);
    this.state = {
      newItem: {
        service: '',
        description: '',
        rate: '',
        units: '',
        total: '',
        taxApplicable: false,
        taxRate: 0
      },
      customService: ''
    };
  }

  // Prevent unnecessary re-renders
  shouldComponentUpdate(nextProps, nextState) {
    if (JSON.stringify(this.props.lineItems) !== JSON.stringify(nextProps.lineItems) ||
        JSON.stringify(this.state) !== JSON.stringify(nextState) ||
        this.props.isDisabled !== nextProps.isDisabled || 
        this.props.chargeOptions !== nextProps.chargeOptions) {
      return true;
    }
    return false;
  }

  componentDidUpdate(prevProps) {
    // When line items change, recalculate tax and push to parent
    if (JSON.stringify(this.props.lineItems) !== JSON.stringify(prevProps.lineItems)) {
      const lineItems = this.props.lineItems || [];
      const totalTax = lineItems.reduce((sum, item) => {
        if (item.taxApplicable && item.taxRate) {
          return sum + ((parseFloat(item.total) || 0) * (parseFloat(item.taxRate) || 0)) / 100;
        }
        return sum;
      }, 0);

      if (this.props.onTaxChange) {
        this.props.onTaxChange(parseFloat(totalTax.toFixed(2)));
      }
    }
  }

  handleAddItem = () => {
    // Validate required fields
    if (!this.state.newItem.service || !this.state.newItem.rate || !this.state.newItem.units) {
      return;
    }
    
    // Get service name (could be custom)
    const serviceName = this.state.newItem.service === 'Custom' ? this.state.customService : this.state.newItem.service;
    
    // Calculate total (pre-tax: rate × units). Tax is tracked separately per line item.
    const rate = parseFloat(this.state.newItem.rate) || 0;
    const units = parseFloat(this.state.newItem.units) || 0;
    const total = (rate * units).toFixed(2);
    
    // Create a copy of the current line items array
    const newLineItems = [...(this.props.lineItems || []), {
      ...this.state.newItem,
      service: serviceName,
      total: total
    }];
    
    // Pass the updated array to the parent
    this.props.onUpdateLineItems(newLineItems);
    
    // Reset the form
    this.setState({
      newItem: {
        service: '',
        description: '',
        rate: '',
        units: '',
        total: '',
        taxApplicable: false,
        taxRate: 0
      },
      customService: ''
    });
  }

  handleDeleteItem = (index) => {
    const newLineItems = [...(this.props.lineItems || [])];
    newLineItems.splice(index, 1);
    this.props.onUpdateLineItems(newLineItems);
  }

  handleChange = (field, value) => {
    this.setState(prevState => {
      const newItem = { ...prevState.newItem, [field]: value };
      
      // Auto-calculate total when rate or units change (pre-tax: rate × units)
      if ((field === 'rate' || field === 'units') && newItem.rate && newItem.units) {
        const rate = parseFloat(newItem.rate) || 0;
        const units = parseFloat(newItem.units) || 0;
        newItem.total = (rate * units).toFixed(2);
      }
      
      return { newItem };
    });
  }

  handleCustomServiceChange = (value) => {
    this.setState({ customService: value });
  }

  handleItemChange = (index, field, value) => {
    // Safely handle undefined lineItems
    if (!this.props.lineItems || !this.props.lineItems[index]) {
      return;
    }
    
    // Create a deep copy of the line items array
    const newLineItems = this.props.lineItems.map((item, i) => 
      i === index ? { ...item } : item
    );
    
    // Update the field
    newLineItems[index][field] = value;
    
    // Auto-calculate total when rate or units change
    if ((field === 'rate' || field === 'units') && newLineItems[index].rate && newLineItems[index].units) {
      const rate = parseFloat(newLineItems[index].rate) || 0;
      const units = parseFloat(newLineItems[index].units) || 0;
      newLineItems[index].total = rate * units;
    }
    
    // Pass the updated array to the parent
    this.props.onUpdateLineItems(newLineItems);
  }

  handleTaxToggle = (index) => {
    // Safely handle undefined lineItems
    if (!this.props.lineItems || !this.props.lineItems[index]) {
      return;
    }
    
    // Create a deep copy of the line items array
    const newLineItems = this.props.lineItems.map((item, i) => 
      i === index ? { ...item } : item
    );
    
    // Toggle tax applicability
    newLineItems[index].taxApplicable = !newLineItems[index].taxApplicable;
    
    // Pass the updated array to the parent
    this.props.onUpdateLineItems(newLineItems);
  }

  handleTaxRateChange = (index, rate) => {
    // Safely handle undefined lineItems
    if (!this.props.lineItems || !this.props.lineItems[index]) {
      return;
    }
    
    // Create a deep copy of the line items array
    const newLineItems = this.props.lineItems.map((item, i) => 
      i === index ? { ...item } : item
    );
    
    // Update tax rate
    newLineItems[index].taxRate = parseFloat(rate) || 0;
    
    // Pass the updated array to the parent
    this.props.onUpdateLineItems(newLineItems);
  }

  render() {
    const { lineItems = [], chargeOptions = [], isDisabled } = this.props;
    const { newItem, customService } = this.state;
    
    // Calculate total of all line items (pre-tax subtotal)
    const subtotal = lineItems.reduce((sum, item) => sum + (parseFloat(item.total) || 0), 0);
    
    // Calculate total tax from all taxable line items
    const totalTax = lineItems.reduce((sum, item) => {
      if (item.taxApplicable && item.taxRate) {
        return sum + ((parseFloat(item.total) || 0) * (parseFloat(item.taxRate) || 0)) / 100;
      }
      return sum;
    }, 0);
    
    const grandTotal = subtotal + totalTax;
    
    return (
      <div className="line-items-container">
        <h6 className="text-blue text-bg-color form-group">Itemized Billing</h6>
        
        <div className="table-responsive">
          <table className="table table-bordered table-hover">
            <thead className="thead-light">
              <tr>
                <th>Service</th>
                <th>Description</th>
                <th>Rate</th>
                <th>Units</th>
                <th>Total</th>
                <th>Tax</th>
                <th>Actions</th>
              </tr>
            </thead>
            <tbody>
              {Array.isArray(lineItems) && lineItems.map((item, index) => (
                <tr key={`line-item-${index}`}>
                  <td>
                    <input 
                      type="text" 
                      className="form-control form-control-sm"
                      value={item.service || ''}
                      disabled={isDisabled}
                      onChange={(e) => this.handleItemChange(index, 'service', e.target.value)}
                    />
                  </td>
                  <td>
                    <input 
                      type="text" 
                      className="form-control form-control-sm"
                      value={item.description || ''}
                      disabled={isDisabled}
                      onChange={(e) => this.handleItemChange(index, 'description', e.target.value)}
                    />
                  </td>
                  <td>
                    <input 
                      type="number" 
                      className="form-control form-control-sm"
                      value={item.rate || ''}
                      disabled={isDisabled}
                      onChange={(e) => this.handleItemChange(index, 'rate', e.target.value)}
                      step="0.01"
                      min="0"
                    />
                  </td>
                  <td>
                    <input 
                      type="number" 
                      className="form-control form-control-sm"
                      value={item.units || ''}
                      disabled={isDisabled}
                      onChange={(e) => this.handleItemChange(index, 'units', e.target.value)}
                      step="0.01"
                      min="0"
                    />
                  </td>
                  <td>
                    <input 
                      type="text" 
                      className="form-control form-control-sm"
                      value={parseFloat(item.total || 0).toFixed(2)}
                      disabled={true}
                      readOnly
                    />
                  </td>
                  <td className="text-center">
                    <div className="custom-control custom-checkbox d-inline-block">
                      <input 
                        type="checkbox" 
                        className="custom-control-input" 
                        id={`taxCheckbox-${index}`}
                        checked={item.taxApplicable || false}
                        disabled={isDisabled}
                        onChange={() => this.handleTaxToggle(index)}
                      />
                      <label className="custom-control-label" htmlFor={`taxCheckbox-${index}`}></label>
                    </div>
                    
                    {item.taxApplicable && (
                      <input 
                        type="number" 
                        className="form-control form-control-sm mt-1"
                        value={item.taxRate || 0}
                        disabled={isDisabled}
                        onChange={(e) => this.handleTaxRateChange(index, e.target.value)}
                        step="0.1"
                        min="0"
                        max="100"
                      />
                    )}
                  </td>
                  <td>
                    <button 
                      type="button" 
                      className="btn btn-danger btn-sm"
                      disabled={isDisabled}
                      onClick={() => this.handleDeleteItem(index)}
                    >
                      <i className="fa fa-trash"></i>
                    </button>
                  </td>
                </tr>
              ))}
              
              {/* Add new item row */}
              <tr>
                <td>
                  <select 
                    className="form-control form-control-sm"
                    value={newItem.service}
                    disabled={isDisabled}
                    onChange={(e) =>{ 
                      let index = chargeOptions.findIndex(charge => charge.chargeName === e.target.value);
                      if(index !== -1){
                        let checked = chargeOptions[index].isTaxable || false;
                        let taxRate = chargeOptions[index].taxRate || 0;
                        let newItem = { ...this.state.newItem, taxApplicable: checked, taxRate: taxRate };
                        //let newItem = { ...this.state.newItem, taxApplicable: checked };
                        this.setState({ newItem });
                      }
                      this.handleChange('service', e.target.value)}}
                  >
                    <option value="">Select Service</option>
                    {chargeOptions.map((option, i) => (
                     <option key={i} value={option.chargeName}>
                     {option.chargeName}
                   </option>
                    ))}
                    <option value="Custom">Custom</option>
                  </select>
                  
                  {newItem.service === 'Custom' && (
                    <input 
                      type="text" 
                      className="form-control form-control-sm mt-1"
                      placeholder="service name"
                      value={customService}
                      disabled={isDisabled}
                      onChange={(e) => this.handleCustomServiceChange(e.target.value)}
                    />
                  )} 
                </td>
                <td>
                  <input 
                    type="text" 
                    className="form-control form-control-sm"
                    value={newItem.description}
                    disabled={isDisabled}
                    placeholder="Description"
                    onChange={(e) => this.handleChange('description', e.target.value)}
                  />
                </td>
                <td>
                  <input 
                    type="number" 
                    className="form-control form-control-sm"
                    value={newItem.rate}
                    disabled={isDisabled}
                    placeholder="Rate"
                    onChange={(e) => this.handleChange('rate', e.target.value)}
                    step="0.01"
                    min="0"
                  />
                </td>
                <td>
                  <input 
                    type="number" 
                    className="form-control form-control-sm"
                    value={newItem.units}
                    disabled={isDisabled}
                    placeholder="Units"
                    onChange={(e) => this.handleChange('units', e.target.value)}
                    step="0.01"
                    min="0"
                  />
                </td>
                <td>
                  <input 
                    type="text" 
                    className="form-control form-control-sm"
                    value={newItem.total ? parseFloat(newItem.total).toFixed(2) : ''}
                    disabled={true}
                    readOnly
                    placeholder="Total"
                  />
                </td>
                <td className="text-center">
                  <div className="custom-control custom-checkbox d-inline-block">
                    <input 
                      type="checkbox" 
                      className="custom-control-input" 
                      id="newItemTaxCheckbox"
                      checked={newItem.taxApplicable || false}
                      disabled={isDisabled}
                      onChange={() =>{ 
                        this.handleChange('taxApplicable', !newItem.taxApplicable)}}
                    />
                    <label className="custom-control-label" htmlFor="newItemTaxCheckbox"></label>
                  </div>
                  
                  {newItem.taxApplicable && (
                    <input 
                      type="number" 
                      className="form-control form-control-sm mt-1"
                      value={newItem.taxRate || 0}
                      disabled={isDisabled}
                      onChange={(e) => this.handleChange('taxRate', e.target.value)}
                      step="0.1"
                      min="0"
                      max="100"
                    />
                  )}
                </td>
                <td>
                  <button 
                    type="button" 
                    className="btn btn-success btn-sm"
                    disabled={isDisabled || !newItem.service || !newItem.rate || !newItem.units || (newItem.service === 'Custom' && !customService)}
                    onClick={this.handleAddItem}
                  >
                    <i className="fa fa-plus"></i> Add
                  </button>
                </td>
              </tr>
            </tbody>
            <tfoot>
              <tr>
                <td colSpan="4" className="text-right"><strong>Subtotal:</strong></td>
                <td><strong>${subtotal.toFixed(2)}</strong></td>
                <td colSpan="2"></td>
              </tr>
              <tr>
                <td colSpan="4" className="text-right"><strong>Tax:</strong></td>
                <td><strong>${totalTax.toFixed(2)}</strong></td>
                <td colSpan="2"></td>
              </tr>
              <tr>
                <td colSpan="4" className="text-right"><strong>Grand Total:</strong></td>
                <td><strong>${grandTotal.toFixed(2)}</strong></td>
                <td colSpan="2"></td>
              </tr>
              <tr>
                <td colSpan="4" className="text-right" style={{color: '#4CAF50'}}>Paid:</td>
                <td style={{color: '#4CAF50'}}>${(this.props.paymentAmt || 0).toFixed(2)}</td>
                <td colSpan="2"></td>
              </tr>
              <tr>
                <td colSpan="4" className="text-right"><strong style={{color: '#E53935'}}>Balance Due:</strong></td>
                <td><strong style={{color: '#E53935'}}>${(grandTotal - (this.props.paymentAmt || 0)).toFixed(2)}</strong></td>
                <td colSpan="2"></td>
              </tr>
            </tfoot>
          </table>
        </div>
      </div>
    );
  }
}

export default LineItems;